//Helpers
import {
	isBluTRV,
	isCamera,
	isDiffuser,
	isPill,
	isShellyPresence,
	isWallDisplay,
} from 'src/helpers/interface_helpers';
import { getURLParams, parseJsonURLParams } from '../../local/core/local_action_parse_url_helpers';
import {
	isValidActionToken,
	validateLnmURLWebhookParams,
	validateLocalActionValue,
	validateURLWebhookParams,
	validateVirtualURLWebhookParams,
} from '../validation/webhook_validation_helpers';
import { getComponentByDeviceMode } from '../../local/core/local_action_components_helpers';
import { isXT1Module } from '../../../xt1_helpers/template_helpers';
import { hasSwitchPeripheral, isPeripheralDevice } from '../../../addons/addon_device_helpers';
import { validateCuryURLWebhook } from '../validation/devices/webhook_cury_validation_helper';
import { validateCameraURLWebhook } from '../validation/devices/webhook_camera_validation_helper';
import { validatePresenceURLWebhook } from '../validation/devices/webhook_presence_validation_helper';
import { getURLWebhookParamsTRVG1 } from './webhook_helpers_gen1';
import { bluTrvDefaultValue } from '../../local/devices/trv_local_actions';
//Types
import type { Device } from 'src/types/Device';
import type { ActionJob } from 'src/types/requests/schedules/schedules_g2';

export const getURLWebhookParams = (url: string, device: Device): ActionJob => {
	const action: ActionJob = {
		method: 'switch.set',
		params: {
			id: 0,
		},
	};
	const params = {},
		decode = (value: string) => {
			try {
				return parseJsonURLParams(decodeURIComponent(value));
			} catch (e) {
				return value;
			}
		},
		decodeURL = (value: string) => {
			try {
				return decodeURIComponent(value);
			} catch (e) {
				return value;
			}
		};
	const xt1Module = isXT1Module(device.type);
	let channels = `[0-${(device?.channels_count || 1) - 1}]`;
	if (xt1Module) {
		channels = '\\d+';
	} else if (device.category == 'emeter') {
		channels = '0';
	} else if (isPeripheralDevice(device?.type) && device.category == 'relay') {
		const componentChannel = Number(device?.component?.split(':')?.[1] ?? 100);
		channels = componentChannel.toString();
	} else if (isPill(device?.type)) {
		channels = '20[0|1|2]';
	} else if (hasSwitchPeripheral(device?.id)) {
		channels += '|100';
	}
	const parseUrlG2 = url.match(/\/rpc\/([^\?]+)/);
	if (parseUrlG2 && /^lnm\.call$/i.test(parseUrlG2[1])) {
		return validateLnmURLWebhookParams(getURLParams(url), device);
	}
	let match: RegExpMatchArray;
	const component = getComponentByDeviceMode(device);
	if (/^https/.test(url)) match = null;
	else if (parseUrlG2) {
		if (!xt1Module && /^(rgbcct|rgbx2light)$/.test(component.deviceMode)) {
			if (component.deviceMode == 'rgbcct') {
				match = url.match(
					new RegExp(
						`\\d\\/rpc\\/((rgb|cct|rgbcct)\\.(set|toggle))\\?(?:[\\w&=]*)?id=(0)\\b`,
						'i',
					),
				);
			} else {
				match =
					url.match(
						new RegExp(
							`\\d\\/rpc\\/((light)\\.(set|toggle))\\?(?:[\\w&=]*)?id=(0|1)\\b`,
							'i',
						),
					) ||
					url.match(
						new RegExp(`\\d\\/rpc\\/((rgb)\\.(set|toggle))\\?(?:[\\w&=]*)?id=(0)\\b`, 'i'),
					);
			}
		} else if (isBluTRV(device.type) && /^blutrv\.call$/i.test(parseUrlG2[1])) {
			match = decodeURL(url).match(
				/\d\/rpc\/((blutrv.call))\?(?:[\w&=]*)?id=(2\d{2})&method=Trv.Set(Target|Boost|Position)&params=\{.*?(target_C|duration|pos)"?:"?((\d+(.\d)?)|-?\$\{ev\.\w+\}).*?\}$/i,
			);
			const method = `${(match?.[4] || 'Target').toLowerCase()}`;
			const param = match?.[5] || 'target_C';
			const value = match?.[6];
			const validateValue = (method: string, param: string, value: any): boolean => {
				switch (method) {
					case 'target':
						return param != 'target_C' || validateLocalActionValue(value, 4, 30);
					case 'boost':
						return param != 'duration' || validateLocalActionValue(value, 0, 35791394);
					case 'position':
						return param != 'pos' || validateLocalActionValue(value, 0, 100);
				}
				return true;
			};
			return {
				method: `blutrv.${method}`,
				params: {
					id: Number(match?.[3] || device.channel - 2000),
					[`${param}`]: isValidActionToken(value)
						? value
						: (Number(value) ?? bluTrvDefaultValue(method)),
				},
				error: match == null || validateValue(method, param, value),
			};
		} else if (isDiffuser(device?.type)) {
			return validateCuryURLWebhook(url);
		} else if (isCamera(device?.type)) {
			return validateCameraURLWebhook(url);
		} else if (isShellyPresence(device?.type)) {
			return validatePresenceURLWebhook(url);
		} else if (isWallDisplay(device.type)) {
			match = url.match(
				new RegExp(
					`\\d\\/rpc\\/(Media.((MediaPlayer.(PlayAudioClip|Play|Stop|Pause))|(Radio.PlayFavourite)))(\\?(id)=(-?\\d+))?((&|\\?)(volume)=(-?\\d+))?\\b`,
					'i',
				),
			);
			if (match) {
				const id = parseInt(match?.[8]) ?? null;
				const volume = parseInt(match?.[12]) ?? null;
				return {
					method: match?.[1],
					params: {
						id,
						volume,
					},
					error:
						(match?.[11] !== undefined && (volume < 0 || volume > 100)) ||
						(match?.[7] !== undefined && !(id >= 0)),
				};
			}
			match = url.match(
				new RegExp(
					`\\d\\/rpc\\/(Thermostat.SetConfig)(\\?(id)=(-?\\d+))?(&|\\?)config=(\\{.*\\})(?=(&|/|$))`,
					'i',
				),
			);
			if (match) {
				let config = match?.[6] || null;
				const id = parseInt(match?.[4]) ?? null;
				return {
					method: match?.[1],
					params: {
						id,
					},
					error: config == null && !(id >= 0),
				};
			}
			match = url.match(
				new RegExp(
					`\\d\\/rpc\\/((switch)\\.(set|toggle))\\?(?:[\\w&=]*)?id=(${channels})\\b`,
					'i',
				),
			);
		} else {
			match =
				url.match(
					new RegExp(
						`\\d\\/rpc\\/((cb|switch|light|cct|rgbcct|rgbw?)\\.(set|toggle))\\?(?:[\\w&=]*)?id=(${channels})\\b`,
						'i',
					),
				) ||
				((xt1Module || device.category == 'roller') &&
					url.match(
						new RegExp(
							`\\d\\/rpc\\/((cover)\\.(open|close|stop|gotoposition))\\?(?:[\\w&=]*)?id=(${channels})\\b`,
							'i',
						),
					)) ||
				url.match(
					new RegExp(
						`\\d\\/rpc\\/((boolean|enum|text|number)\\.(set))\\?(?:[\\w&=]*)?id=(2\\d{2})\\b`,
						'i',
					),
				) ||
				url.match(
					new RegExp(`\\d\\/rpc\\/((button)\\.(trigger))\\?(?:[\\w&=]*)?id=(2\\d{2})\\b`, 'i'),
				) ||
				url.match(
					new RegExp(`\\d\\/rpc\\/((rgbcct)\\.(setconfig))\\?(?:[\\w&=]*)?id=(0)\\b`, 'i'),
				);
		}
	} else if (device.type == 'SHTRV-01') {
		return getURLWebhookParamsTRVG1(url, decodeURL);
	} else {
		match = url.match(new RegExp(`\\d\\/(relay|roller|light|color)\\/(${channels}\\b)\/?`, 'i'));
	}
	let category: string = (match?.[1] || '').toLowerCase();
	const virtualAction: boolean = parseUrlG2 && /^(boolean|enum|text|button|number)$/i.test(match?.[2]);
	const deviceMode = component.mode;
	if (
		!category ||
		(!virtualAction &&
			!xt1Module &&
			((parseUrlG2 &&
				/^(cct|rgbcct|rgbw?)\.(set|toggle)/.test(category) &&
				!category.startsWith(deviceMode)) ||
				(/^(rgbcct)\.(setconfig)/.test(category) && !/^(rgbcct)$/.test(deviceMode)) ||
				(/^(cb)\.(set)/.test(category) && !/^(breaker)$/.test(deviceMode)) ||
				(/^(relay|emeter)/.test(device.category) && !/^(relay|switch)/.test(category)) ||
				(/^(roller)/.test(device.category) && !/^(cover|roller)/.test(category)) ||
				(/^(light|color)/.test(device.category) && /^(relay|roller|switch)/.test(category))))
	) {
		action.error = true;
		return action;
	}

	if (parseUrlG2) {
		action.params.id = Number(match?.[4]) || 0;
		action.method = match?.[1];
	} else {
		action.params.id = Number(match?.[2]) || 0;
		if (/color/.test(category) || deviceMode == 'color') {
			action.method = 'rgbw.set';
		} else if (/light/.test(category)) {
			action.method = `${deviceMode == 'white' ? 'light' : deviceMode}.set`;
		}
	}

	const urlParamsStr = url.split('?')?.[1];
	if (urlParamsStr) {
		const urlParams = urlParamsStr.split('&');
		for (const k in urlParams) {
			const split = urlParams[k].split('=');
			const key = split[0];
			const val = split[1];
			if (key == '' || val == '') {
				action.error = true;
				return action;
			}
			params[key] = decode(val);
		}
	}

	for (let k in params) {
		k = k.toLowerCase();
		let param = params[k];
		if (k == 'id' || k == 'a2') continue;
		if (/^(true|false)$/.test(param)) param = /^(true)$/.test(param);
		else if (param && !isNaN(param)) param = Number(param);
		if (parseUrlG2) {
			action.params[k] = param;
		} else if (category == 'roller') {
			if (k == 'roller_pos') {
				action.params.pos = param;
			} else if (k == 'go') {
				if (/^(close|open)$/.test(param)) {
					action.method = 'cover.' + param;
				} else if (param == 'to_pos') {
					action.method = 'cover.gotoposition';
				} else {
					action.error = true;
				}
			} else if (k == 'duration') {
				action.params.toggle_after = param;
			} else {
				action.error = true;
			}
		} else {
			if (k == 'turn') {
				if (param == 'toggle') {
					action.method = action.method.replace('.set', '.toggle');
				} else if (/^(on|off)$/.test(param)) {
					action.params.on = param == 'on';
				} else {
					action.error = true;
				}
			} else if (k == 'timer') {
				action.params.toggle_after = param;
			} else if (/(light|color|white)/.test(category)) {
				if (/white/.test(k)) {
					action.params[k] = param;
				} else if (k == 'temp' && component.colorTemp) {
					action.params.ct = param;
				} else if (k == 'transition') {
					action.params.transition_duration = param;
				} else if (
					(deviceMode != 'color' && k == 'brightness') ||
					(deviceMode == 'color' && k == 'gain')
				) {
					action.params.brightness = param;
				} else if (deviceMode == 'color' && /(red|blue|green)/.test(k)) {
					if (!action.params.rgb) action.params.rgb = [0, 0, 0];
					if (k == 'red') action.params.rgb[0] = param;
					else if (k == 'green') action.params.rgb[1] = param;
					else if (k == 'blue') action.params.rgb[2] = param;
				} else {
					action.error = true;
				}
			} else {
				action.error = true;
			}
		}
	}
	return virtualAction
		? validateVirtualURLWebhookParams(action, device)
		: validateURLWebhookParams(action, device);
};
